import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Button,
    Grid,
    Box,
    CircularProgress,
    FormControl,
    TextField,
    RadioGroup,
    FormLabel,
    Radio,
    TableContainer,
    TableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    FormControlLabel,
    IconButton,
    Modal,
    Typography,
    Stack,
    Checkbox,
    Autocomplete,
    Tooltip,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
    deleteMemberAllergy,
    getMemberAllergies,
    gotoNextMenu,
    gotoPreviousMenu,
    saveMemberAllergy,
    toggleShowAlleryModal,
} from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import classes from './Styles';
import { MemberAllergy } from '../../../Models/Member/MemberAllergy.model';
import { AllergyTypeEnum } from '../../../Enum/AllergyTypeEnum';
import { AllergyTypes } from '../../../utils/assessments';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { saveEngagementDraft } from '../../../store/documentEngagement.slice';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { copyObject } from '../../../utils/common';
import { updateNoKnownAllergies } from '../../../store/memberDetail.slice';
import { apiString as API_URL } from '../../../utils/constants';
import axios from 'axios';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Education_Telemonitoring_Advanced_Directives_Allergies.pdf';

const memberAllergyFormSchema = Yup.object().shape({
    Name: Yup.string().nullable().required('Allergy Name is required.'),
    Reactions: Yup.array().min(1, 'At least one reaction is required.').required('Reaction is required.'),
    OtherReaction: Yup.string().when('Reactions', {
        is: (reactions: string[]) => reactions?.includes('Other'),
        then: (schema) => schema.required('Please describe the other reaction.'),
        otherwise: (schema) => schema.nullable(),
    }),
});

type AllergyReaction = { Id: string; Name: string; DisplayOrder: number; IsActive: boolean };

const MemberAllergyComponent: React.FC<{ memberId: string; isAssessment: boolean }> = ({ memberId, isAssessment }) => {
    const dispatch = useAppDispatch();
    const [selectedAllergy, setSelectedAllergy] = useState<MemberAllergy>(new MemberAllergy());
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
    const [medRxNorms, setMedRxNorms] = useState([]);
    const [allergyClasses, setAllergyClasses] = useState([]);
    const [showClasses, setShowClasses] = useState<boolean>(false);
    const [allergyReactions, setAllergyReactions] = useState<AllergyReaction[]>([]);
    const {
        memberAllergies,
        showAlleryModal,
        deletingAlergyIds,
        isSaving: isSavingAssessment,
        isFetching,
    } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const { member } = useSelector((state: RootState) => state.memberDetail);
    const isSaving = isSavingAssessment || isSavingEngagementDraft;
    useEffect(() => {
        dispatch(getMemberAllergies(memberId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/allergies/GetAllergyClasses`).then((response) => {
            setAllergyClasses(response.data);
        });
        setShowClasses(false);
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/allergies/GetMedicationRxNorms`).then((response) => {
            setMedRxNorms(response.data);
        });
    }, []);

    useEffect(() => {
        axios.get(`${API_URL}/allergies/GetAllergyReactions`).then((response) => {
            setAllergyReactions(response.data);
        });
    }, []);

    const handleSubmit = (values: MemberAllergy) => {
        setIsChanged(true);
        dispatch(saveMemberAllergy(values));
    };

    const handleOnCloseModal = () => dispatch(toggleShowAlleryModal(false));

    const handleOnShowModal = (allergy = new MemberAllergy()) => {
        setSelectedAllergy({ ...allergy, MemberId: memberId });
        dispatch(toggleShowAlleryModal(true));
        setShowClasses(allergy.AllergyType === AllergyTypeEnum.Class);
    };

    const handleOnClickDelete = (allergy: MemberAllergy) => {
        setSelectedAllergy(allergy);
        setShowDeleteAlert(true);
    };

    const handleOnConfirmedDelete = () => {
        dispatch(deleteMemberAllergy(selectedAllergy.Id));
        setShowDeleteAlert(false);
    };

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = () => {
        dispatch(gotoNextMenu());
    };

    const handleOnConfirm = () => {
        let data = copyObject(engagementDraft);
        data.SummaryNotes = data.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.Allergy) || [];
        data.SummaryNotes.push({
            Id: '',
            CptCode: '',
            Zcode: '',
            Note: 'Completed Allergy Information',
            Type: SummaryNoteTypeEnum.Allergy,
            IsConfirm: true,
            OrderBy: 32,
            DisplayName: 'Allergy',
        });
        const updatedMemberAssessmentStatuses = {
            ...memberAssessmentHistory.MemberAssessmentStatuses,
            AllergyStatus: StatusEnum.Completed,
        };
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: data,
            MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
        };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, true));
    };

    const renderAddOrUpdateAllergy = () => {
        return (
            <Modal open={showAlleryModal} onClose={handleOnCloseModal} disableEnforceFocus>
                <Formik initialValues={selectedAllergy} enableReinitialize={true} onSubmit={handleSubmit} validationSchema={memberAllergyFormSchema}>
                    {({ values, setFieldValue, errors, handleChange, handleBlur, validateForm, submitCount }: FormikProps<MemberAllergy>) => (
                        <Form noValidate>
                            <Box sx={{ ...classes.modalPopupForm, ...classes.form }}>
                                <div className="mui-modal-header">
                                    <Typography variant="h6" component="h6">
                                        Allergy & Reaction
                                    </Typography>
                                </div>
                                <div className="mui-modal-body">
                                    <Grid container spacing={2}>
                                        <Grid size={{ md: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Name"
                                                variant="outlined"
                                                value={values.Name}
                                                name="Name"
                                                onChange={handleChange}
                                                error={Boolean(submitCount) && Boolean(errors?.Name)}
                                                helperText={Boolean(submitCount) ? errors?.Name : ''}
                                            />
                                        </Grid>

                                        <Grid size={12}>
                                            <Stack direction={'row'} spacing={2} alignItems={'flex-end'}>
                                                <FormControl>
                                                    <FormLabel id="allergy-type-radio-buttons-group-label">Allergy Type</FormLabel>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="allergy-type-radio-buttons-group-label"
                                                        value={values.AllergyType}
                                                        name="AllergyType"
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            if (parseInt(e.target.value) === AllergyTypeEnum.Class) {
                                                                setShowClasses(true);
                                                            } else {
                                                                setShowClasses(false);
                                                            }
                                                        }}
                                                    >
                                                        <FormControlLabel value={AllergyTypeEnum.Medication} control={<Radio />} label="Medication" />
                                                        <FormControlLabel value={AllergyTypeEnum.Class} control={<Radio />} label="Class" />
                                                    </RadioGroup>
                                                </FormControl>
                                                {showClasses ? (
                                                    <Autocomplete
                                                        id="allergy-class-select"
                                                        options={allergyClasses.sort((a, b) => a.ClassName.localeCompare(b.ClassName))}
                                                        getOptionLabel={(option) => option.ClassName}
                                                        style={{ width: 250 }}
                                                        renderInput={(params) => <TextField {...params} label="Allergy Class" variant="outlined" />}
                                                        onChange={(e, value) => {
                                                            setFieldValue('AllergyClass', value?.ClassNumber || '');
                                                        }}
                                                        value={allergyClasses.find((a) => a.ClassNumber === values.AllergyClass) || null}
                                                    />
                                                ) : (
                                                    <Autocomplete
                                                        id="rx-norm-code-select"
                                                        options={medRxNorms.sort((a, b) => a.Name.localeCompare(b.Name))}
                                                        getOptionLabel={(option) => `${option.RxNorm} - ${option.Name} ${option.Strength} ${option.UOM}`}
                                                        style={{ width: 250 }}
                                                        renderInput={(params) => <TextField {...params} label="RxNorm Code" variant="outlined" />}
                                                        onChange={(e, value) => {
                                                            setFieldValue('RxNormCode', value?.RxNorm || '');
                                                        }}
                                                        value={medRxNorms.find((a) => a.RxNorm === values.RxNormCode) || null}
                                                        filterOptions={(options, { inputValue }) => {
                                                            if (inputValue.length < 2) {
                                                                return [];
                                                            } else {
                                                                return options.filter(
                                                                    (option) =>
                                                                        option.Name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                                        option.RxNorm.toLowerCase().includes(inputValue.toLowerCase())
                                                                );
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </Grid>
                                        <Grid size={{ md: 12 }}>
                                            <Autocomplete
                                                multiple
                                                id="allergy-reactions-select"
                                                options={allergyReactions}
                                                getOptionLabel={(option) => option.Name}
                                                value={allergyReactions.filter((r) => values.Reactions?.includes(r.Name))}
                                                onChange={(_, value) => {
                                                    setFieldValue(
                                                        'Reactions',
                                                        value.map((v) => v.Name)
                                                    );
                                                    if (!value.some((v) => v.Name === 'Other')) {
                                                        setFieldValue('OtherReaction', '');
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Reaction(s)"
                                                        variant="outlined"
                                                        error={Boolean(submitCount) && Boolean(errors?.Reactions)}
                                                        helperText={Boolean(submitCount) ? (errors?.Reactions as string) : ''}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        {values.Reactions?.includes('Other') && (
                                            <Grid size={{ md: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Please describe the other reaction"
                                                    variant="outlined"
                                                    size="small"
                                                    value={values.OtherReaction || ''}
                                                    name="OtherReaction"
                                                    onChange={handleChange}
                                                    error={Boolean(submitCount) && Boolean(errors?.OtherReaction)}
                                                    helperText={Boolean(submitCount) ? errors?.OtherReaction : ''}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </div>
                                <div className="mui-modal-footer">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        size="small"
                                        disabled={isSaving}
                                        endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        Save
                                    </Button>
                                    <Button size="small" type="button" onClick={handleOnCloseModal} variant="outlined" disabled={isSaving}>
                                        Cancel
                                    </Button>
                                </div>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Modal>
        );
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            {isFetching ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                        <div className="d-row-space-between">
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                <Typography variant="h6">Allergies / Reactions</Typography>
                                <Tooltip title="Allergies / Reactions Help Document">
                                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                </Tooltip>
                            </Stack>
                            <Stack direction="row" justifyContent="center" spacing={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={member.NoKnownAllergies}
                                            onChange={(e) => {
                                                dispatch(updateNoKnownAllergies(memberId, e.target.checked));
                                                setIsChanged(true);
                                            }}
                                        />
                                    }
                                    label="NKA"
                                    labelPlacement="start"
                                    sx={classes.nkaCheckbox}
                                />
                                <IconButton onClick={() => handleOnShowModal()} sx={{ p: 0, pr: 3 }}>
                                    <AddBoxRoundedIcon color="primary" fontSize="large" />
                                </IconButton>
                            </Stack>
                        </div>
                        <hr style={classes.hr} />
                    </Grid>

                    <Grid className="content-form" sx={{ p: 0 }}>
                        {memberAllergies.length ? (
                            <TableContainer sx={classes.tableContainer}>
                                <Typography variant="h6" component="label" sx={{ fontSize: '14px', ml: 1 }}>
                                    <span style={{ color: 'red' }}>* </span>Downloaded from Veradigm
                                </Typography>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="20%">Medication</TableCell>
                                            <TableCell width="10%">Type</TableCell>
                                            <TableCell width="50%">Reaction</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {memberAllergies.map((allergy, index) => (
                                            <TableRow
                                                key={`summary-note-${allergy.Id}-${index}`}
                                                hover
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell>
                                                    {allergy.Name}
                                                    {allergy.Source === 'Veradigm' ? <span style={{ color: 'red' }}> *</span> : null}
                                                </TableCell>
                                                <TableCell>{AllergyTypes.find((a) => String(a.Id) === String(allergy.AllergyType)).Name}</TableCell>
                                                <TableCell>
                                                    <div className="d-row-space-between">
                                                        <span>
                                                            {allergy.Reactions?.length
                                                                ? allergy.Reactions.map((r) =>
                                                                      r === 'Other' && allergy.OtherReaction ? `Other: ${allergy.OtherReaction}` : r
                                                                  ).join(', ')
                                                                : allergy.ReactionsDisplay}
                                                        </span>
                                                        <div className="row">
                                                            <IconButton onClick={() => handleOnShowModal(allergy)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => handleOnClickDelete(allergy)}
                                                                sx={{ mr: 2 }}
                                                                disabled={deletingAlergyIds.some((d) => d === allergy.Id)}
                                                            >
                                                                {deletingAlergyIds.some((d) => d === allergy.Id) ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <DeleteIcon color="error" fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography sx={{ ml: 3 }} variant="body1" component="label">
                                No allergies for this member.
                            </Typography>
                        )}
                    </Grid>
                    {isAssessment ? (
                        <Grid justifyContent="flex-end" className="content-footer">
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                sx={{ mr: 3 }}
                                disabled={isSaving}
                                onClick={() => dispatch(gotoPreviousMenu())}
                                startIcon={<ArrowBackIosIcon />}
                            >
                                Previous
                            </Button>
                            <Button type="submit" variant="contained" disabled={isSaving} onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                                Next
                            </Button>
                        </Grid>
                    ) : null}
                    {renderAddOrUpdateAllergy()}

                    <AlertDialog
                        open={showDeleteAlert}
                        title="Delete Allergy"
                        okText="Confirm"
                        onConfirm={handleOnConfirmedDelete}
                        isLoading={isSaving}
                        onClose={() => {
                            setShowDeleteAlert(false);
                        }}
                        message="Are you sure want to delete this allergy?"
                    />
                </>
            )}
            {Boolean(memberAssessmentHistory.MemberAssessmentStatuses.AllergyStatus === StatusEnum.InProgress) ||
            Boolean(memberAssessmentHistory.MemberAssessmentStatuses.AllergyStatus === StatusEnum.Completed) ||
            isChanged ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Allergy"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={handleOnConfirm}
                    onSkip={handleOnConfirmToNext}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="I confirm and acknowledge that Allergy is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Allergy"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={handleOnConfirmToNext}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="Are you sure you want to proceed without making changes to Allergy?"
                />
            )}
        </Grid>
    );
};

export default MemberAllergyComponent;
